import { scopeStringDevStorageKey } from "./StringDevStorageScope";
const KEY_PREFIX = "stdg";

export interface StringDevPersistedStateOptions<T> {
  moduleId: string;
  name: string;
  default: T;
  validate?: (raw: unknown) => T | null;
  legacyKeys?: string[];
}

export class StringDevPersistedState<T> {
  private readonly baseStorageKey: string;
  private readonly defaultValue: T;
  private readonly validate?: (raw: unknown) => T | null;
  private readonly legacyKeys: string[];

  constructor(options: StringDevPersistedStateOptions<T>) {
    this.baseStorageKey = `${KEY_PREFIX}:${options.moduleId}:${options.name}`;
    this.defaultValue = options.default;
    this.validate = options.validate;
    this.legacyKeys = options.legacyKeys ?? [];
  }

  public read(): T {
    const current = this.readRaw(this.storageKey);
    if (current !== undefined) {
      return current;
    }

    for (const legacyKey of this.legacyKeys) {
      const scopedLegacyKey = scopeStringDevStorageKey(legacyKey);
      const migrated = this.readRaw(scopedLegacyKey);
      if (migrated !== undefined) {
        this.write(migrated);
        this.clearRaw(scopedLegacyKey);
        return migrated;
      }
    }

    return this.defaultValue;
  }

  public write(value: T): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch {
      /* quota, access denied, etc. */
    }
  }

  public clear(): void {
    this.clearRaw(this.storageKey);
  }

  private get storageKey(): string {
    return scopeStringDevStorageKey(this.baseStorageKey);
  }

  private readRaw(key: string): T | undefined {
    let raw: string | null;
    try {
      raw = localStorage.getItem(key);
    } catch {
      return undefined;
    }
    if (raw == null) return undefined;

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return undefined;
    }

    if (this.validate) {
      const validated = this.validate(parsed);
      return validated == null ? undefined : validated;
    }

    return parsed as T;
  }

  private clearRaw(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}
